import React, { useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  addConnectorRating,
  fetchAllConnectorRatings,
} from '../../../redux/slices/ConnectorRatingSlice';
import MyButton from '../../../shared-components/button/Button';
import CheckBoxButtons from '../../../shared-components/check-box-buttons/CheckBoxButtons';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';

interface ModalInterface {
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
}

interface AddRatingProperties {
  closeModal: () => void;
}

/**
 * Add Rating component
 * @param {any} param0 - The param0
 * @param {any} param0.closeModal - The closeModal prop
 * @returns {any} JSX.Element
 */
function AddRating({ closeModal }: AddRatingProperties) {
  const dispatch = useAppDispatch();
  const [ratingDetails, setRatingDetails] = useState({
    ratingName: '',
    currentType: 'AC',
    maxPowerOutput: '',
    active: true,
  });
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [modalView, setModalView] = useState<ModalInterface>({
    title: '',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: false,
  });
  const { page, limit } = useAppSelector((state) => state.connectorRating);

  /**
   * Handle the input change function
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleInputChange = (name: string, value: string | number | boolean) => {
    if (name === 'maxPowerOutput') {
      const input = String(value);
      if (!/^\d*\.?\d{0,2}$/.test(input)) {
        return;
      }
    }
    setRatingDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle the submit function
   */
  const handleSubmit = async () => {
    setShowFormErrors(true);
    if (ratingDetails.ratingName === '' || ratingDetails.maxPowerOutput === '') {
      return;
    }
    setDisabled(true);
    try {
      await dispatch(
        addConnectorRating({
          connectorRatingName: ratingDetails.ratingName,
          type: ratingDetails.currentType,
          maxPowerOutput: ratingDetails.maxPowerOutput,
          status: ratingDetails.active,
        }),
      ).unwrap();
      dispatch(
        fetchAllConnectorRatings({
          page,
          limit,
        }),
      );
      setOpenStatus(true);
      setModalView({
        title: '',
        header: false,
        rightCorner: '',
        notification: true,
        content: (
          <SuccessNotification
            title="Connector Rating Added Successfully"
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false);

              closeModal();
            }}
          />
        ),
      });
    } catch (error) {
      console.log(error);
    } finally {
      setDisabled(false);
    }
  };

  /**
   * Handle the submit function
   */
  // const handleSubmit = () => {

  return (
    <>
      <ModalBox
        open={openStatus}
        header={modalView.header}
        title={modalView.title}
        notification={modalView.notification}
        rightCorner={modalView.rightCorner}
        content={modalView.content}
        closeModal={() => setOpenStatus(false)}
      />
      <div className="add-rating-side-panel">
        <div className="panel-header">
          <p>Add New Connector Ratings</p>
        </div>

        <div style={{ height: window.innerHeight >= 700 ? 398 : 710 }}>
          <div className="add-rating-group">
            <div className="profile-card">
              <div className="edit-fields-container">
                <div className="info-row-input-single">
                  <CustomInput
                    name="ratingName"
                    inputLabel="Rating Name"
                    type="text"
                    value={ratingDetails.ratingName}
                    placeholder="Enter Rating Name (eg.AC Connector 3.3KW)"
                    onChangeValue={handleInputChange}
                    valueRequired
                    error={showFormErrors && ratingDetails.ratingName === ''}
                    errorMessage="Rating Name is Required."
                  />
                </div>

                <div className="info-row-input">
                  <SelectField
                    name="currentType"
                    inputLabel="Current Type"
                    selectedValue={ratingDetails.currentType}
                    placeholder="Select Type"
                    onChangeValue={handleInputChange}
                    options={[
                      {
                        label: 'AC',
                        value: 'AC',
                      },
                      {
                        label: 'DC',
                        value: 'DC',
                      },
                      {
                        label: 'Hybrid',
                        value: 'Hybrid',
                      },
                    ]}
                  />

                  <CustomInput
                    name="maxPowerOutput"
                    inputLabel="Max Power Output"
                    type="text"
                    value={ratingDetails.maxPowerOutput}
                    placeholder="Enter Power (eg. 50.20)"
                    onChangeValue={handleInputChange}
                    valueRequired
                    error={showFormErrors && ratingDetails.maxPowerOutput === ''}
                    errorMessage="Max Power Output is Required."
                    svgIcon={
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#666',
                          paddingRight: '4px',
                        }}>
                        kW
                      </span>
                    }
                  />
                </div>

                <div className="checkbox-section">
                  <CheckBoxButtons
                    name="active"
                    list={[
                      {
                        label: 'Marks As Active',
                        value: 'active',
                      },
                    ]}
                    value={ratingDetails.active ? ['active'] : []}
                    onChangeValue={(name: string, value: any) => {
                      handleInputChange(name, value.includes('active'));
                    }}
                  />
                  <p className="checkbox-description">
                    Inactive Ratings Will Not Be Available For Selection
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="footer-actions">
            <MyButton
              label="Cancel"
              buttonType="outline"
              onClickFunc={() => {
                closeModal();
              }}
            />

            <MyButton
              disableButton={disabled}
              label="Save"
              buttonType="submit"
              onClickFunc={handleSubmit}
              loading={disabled}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default AddRating;
